import React from 'react'

import {makeAutoObservable, observable} from "mobx"
import {DeleteOutlined, PlusCircleOutlined, BulbOutlined, TagOutlined, ExportOutlined} from "@ant-design/icons";

import {
	IncidentsGridStoreCustomConfigurationValues,
	IncidentsGridStoreProps
} from "areas/incidents/IncidentsGridStore/types";
import {AutoReloadPlugin} from "controls/grid/plugins/autoReloadPlugin";
import {Incident} from "areas/incidents/incident";
import {IncidentEvent} from "areas/incidents/events/incidentEvent";
import {GridMenuPlugin} from "controls/grid/plugins/grid-menu/gridMenuPlugin";
import {
	changeIncidentsStatus,
	deleteIncidents, exportToCsv,
	getIncidents,
	getIncidentsRulesConfiguration, getIncidentStatuses,
	setIncidentTags
} from "areas/incidents/IncidentsGridStore/api";
import {CeeviewNavigator} from "tools/ceeviewNavigator";
import {GridColumnConfig} from "controls/grid/gridColumnConfig";
import {ApplicationState} from "framework/applicationState";
import {Permission} from "framework/permission";
import {apiFetch} from "framework/api";
import {GridStore} from "controls/grid/gridStore";
import {RemoteDataProvider} from "controls/grid/remoteDataProvider";
import {GridConfiguration, GridSelectionType} from "controls/grid/gridConfiguration";
import {UserSettingsStateProvider} from "controls/grid/stateProviders";
import {opesSetTagsForm} from "controls/react/tagsSelect";
import {GridSelection} from "controls/grid/gridSelection";
import {Link} from "controls/react/link";
import {IncidentsRouter} from "areas/incidents/bundleDescription";
import {NavigationStore} from "framework/navigationStore";
import {openModal} from "controls/react/ant/antModal";
import {Section} from "controls/react/layout/section";
import {FormEntryNew} from "controls/react/form/formEntryNew";
import {AntSelect} from "controls/react/ant/antSelect";
import Settings from "settings";
import {GridMenuAction} from "controls/grid/plugins/grid-menu/gridMenuAction";
import {StateOverridePlugin} from "controls/grid/plugins/stateOverridePlugin";
import {dateToString} from "tools/dateTimeUtils";


const i18n = require('core/localization').translator({
	'All selected items should belong to the same account and be of the same type': {},
	'Are you sure you want to delete incident?': {
		no: 'Dette vil slette sak?'
	},
	'Are you sure you want to delete {0} incidents?': {
		no: 'Vil du slette sakene?'
	},
	'Change status': {
		en: 'Change status',
		no: 'Endre status',
	}
});

export class IncidentsGridStore {
	gridStore: GridStore<Incident>
	menuRef: React.MutableRefObject<HTMLElement>
	props: IncidentsGridStoreProps

	constructor(props?: IncidentsGridStoreProps) {
		this.props = props;
		makeAutoObservable(this)
	}

	initDefault(gridConfigOverrides?: Partial<GridConfiguration<Incident>>,
	            customValues?: IncidentsGridStoreCustomConfigurationValues) {

		let gridConfigActual: GridConfiguration<Incident> = {
			columns: this.getColumns(),
			dataProvider: new RemoteDataProvider({
				dataSource: () => this.getDefaultDatasource(),
				filtersSource: this.getDefaultFilters()
			}),
			plugins: [
				this.getAutoReloadPlugin(),
				this.getGridMenuPlugin(),
				//this.getDraggablePlugin(),
				new StateOverridePlugin({
					filters: this.props?.filters,
					dataObject: this.props,
					enabled: this.props?.entityId == null ? undefined : true
				})
			],
			selection: GridSelectionType.Many
		}

		if (customValues?.userSettingsKey) {
			gridConfigActual.stateProviders = [
				new UserSettingsStateProvider({
					category: customValues.userSettingsKey
				})
			]
		}

		if (gridConfigOverrides) {
			Object.assign(gridConfigActual, gridConfigOverrides)
		}

		this.gridStore = new GridStore<Incident>(gridConfigActual)
	}

	get title() {
		return i18n('Incidents')
	}

	getAutoReloadPlugin() {
		return new AutoReloadPlugin<Incident>({
			subscriptions: [
				IncidentEvent.subscription({
					accountIds: [ApplicationState.accountId],
					includeSubaccounts: ApplicationState.includeSubaccounts
				})
			]
		})
	}

	getGridMenuPlugin() {
		return new GridMenuPlugin<Incident>({
			actions: this.menuItems
		})
	}

	get menuItems() {
		return [
			{
			id: 'create',
			icon: <PlusCircleOutlined/>,
			label: i18n('Create'),
			action: () => {
				NavigationStore.go(IncidentsRouter.createNew('GENERIC'));
				return false;
			},
			visible: () => ApplicationState.hasPermissions(Permission.IncidentCreate)
		},
			{
			id: 'delete',
			icon: <DeleteOutlined />,
			label: i18n('Delete'),
			enabled: (selection: GridSelection<Incident>) => selection.isAnythingSelected,
			confirmation: {
				singularQuestion: i18n('Are you sure you want to delete incident?'),
				pluralQuestion: i18n('Are you sure you want to delete {0} incidents?'),
			},
			action: async (selection: GridSelection<Incident>) => {
				const result = await apiFetch(deleteIncidents(this.gridStore, selection));

				if (result.success) {
					this.gridStore.dataProvider.silentReload();
					this.gridStore.selection.clear();
				}
				return result.success;
			},
			visible: () => ApplicationState.hasPermissions(Permission.IncidentDelete)
		},
			{
			id: 'change-status',
			icon: <BulbOutlined />,
			label: i18n('Change status'),
			visible: () => ApplicationState.hasPermissions(Permission.IncidentUpdate),
			enabled: (selection: GridSelection<Incident>) => {
				if (selection.selectedItemsCount == 0)
					return false

				const {accountId, contentCategory} = selection.dataset.items[0].data

				return {
					enabled: selection.dataset.items.every(x => x.data.accountId == accountId && x.data.contentCategory == contentCategory),
					reason: i18n('All selected items should belong to the same account and be of the same type')
				}
			},
			action: async (selection: GridSelection<Incident>) => {
				const {accountId, contentCategory} = selection.dataset.items[0].data
				const statuses = await apiFetch(getIncidentStatuses({accountId, contentCategory}))

				const model = observable({
					status: statuses.data[0].value
				})

				const data = openModal({
					title: i18n('Update status'),
					okText: i18n('Update'),
					onOk: async () => {
						const result = await apiFetch(changeIncidentsStatus({
							store: this.gridStore,
							selection: selection,
							status: model.status,
							contentCategory,
							accountId
						}))
						if (result.success) {
							this.gridStore.dataProvider.silentReload();
							this.gridStore.selection.clear();
						}
						return result.success;
					}
				}, <Section>
					<FormEntryNew label={i18n('Status')} model={model} modelField={"status"}>
						<AntSelect options={statuses.data}/>
					</FormEntryNew>
				</Section>)

				return data.promise
			}
		},{
			id: 'tags',
			icon: <TagOutlined />,
			label: i18n('Set Tags'),
			action: this.showSetTagsForm,
			visible: () => ApplicationState.hasPermissions(Permission.IncidentUpdate),
			enabled: (selection: GridSelection<Incident>) => selection.isAnythingSelected,
		},{
			id: 'export-csv',
			icon: <ExportOutlined />,
			label: i18n('Export to CSV'),
			action: async (selection: GridSelection<Incident>) => {
				const result = await apiFetch<string>(exportToCsv(this.gridStore, selection));

				if (result.success) {
					window.open(Settings.serverPath + 'sessions/exportCSV/' + result.data, '_blank');
					window.focus();
					this.gridStore.dataProvider.silentReload();
					this.gridStore.selection.clear();
				}
				return result.success
			}
		}] as GridMenuAction<Incident>[]
	}

	getDefaultDatasource(props?: IncidentsGridStoreProps) {
		return getIncidents(props ?? this.props)
	}

	getDefaultFilters = () => {
		return getIncidentsRulesConfiguration()
	}

	columns: GridColumnConfig<Incident>[];

	getColumns(navigator?: CeeviewNavigator) {
		if (this.columns == null) {
			this.columns = [{
				field: 'status',
				title: i18n('Status'),
				width: 100
			},{
				field: 'incidentNumber',
				title: i18n('Number'),
				renderer: (item) => <Link navigator={navigator} url={IncidentsRouter.details(item.id)}>{item.incidentNumber}</Link>,
				width: 40
			},{
				field: 'subject',
				title: i18n('Subject'),
				width: 150
			},{
				field: 'accountName',
				title: i18n('Account'),
				width: 100
			},{
				field: 'tags',
				title: i18n('Tags'),
				width: 100,
				renderer: item => item.tags.join(", "),
			},{
				field: 'contentType',
				title: i18n('Type'),
				width: 80
			},{
				field: 'priorityName',
				title: i18n('Priority'),
				width: 100
			},{
				field: 'impactName',
				title: i18n('Impact'),
				width: 80
			},{
				field: 'owner',
				title: i18n('Owner'),
				width: 80
			},{
				field: 'created',
				title: i18n('Created'),
				renderer: (item) => dateToString(item.created, 'datetime'),
				width: 100
			},{
				field: 'modified',
				title: i18n('Modified'),
				renderer: (item) => dateToString(item.created, 'datetime'),
				width: 100
			},{
				field: 'createdBy',
				title: i18n('Reported by'),
				width: 100
			},{
				field: 'assignedTo',
				title: i18n('Assigned to'),
				width: 100
			}, {
				field: 'description',
				title: i18n('Description'),
				minWidth: 100
			}]
		}

		return this.columns;
	}

	showSetTagsForm = async (selection: GridSelection<Incident>) => {
		let tags = await opesSetTagsForm({anchor: this.menuRef})

		if (tags) {
			await this.setTags(selection, tags)
		}
	}

	setTags = async (selection: GridSelection<Incident>, tags: string[]) => {
		const result = await apiFetch(setIncidentTags(this.gridStore, selection, tags))

		if (result.success) {
			this.gridStore.dataProvider.silentReload();
			this.gridStore.selection.clear();
		}
		return result.success;
	}

	// getDraggablePlugin(){
	// 	return new DraggablePlugin<Incident>({
	// 		entityType: EntityType.Sla,
	// 		attributes: ['serviceId']
	// 	})
	// }
}
